import { animate, state, style, transition, trigger } from '@angular/animations'
import { ApplicationRef, Component, ErrorHandler, OnDestroy, OnInit, Renderer2, ViewChild, ViewEncapsulation, ChangeDetectionStrategy } from '@angular/core'
import { NavigationStart, Router, RouterOutlet } from '@angular/router'

import { MatDialog, MatDialogConfig } from '@angular/material/dialog'
import { MatSidenav } from '@angular/material/sidenav'

import { AiiaDialogService } from './_angular/service/aiia-dialog.service'
import { KayttajaService } from './_angular/service/kayttaja.service'
import { LaskuService } from './_angular/service/lasku/lasku.service'
import { LemonHttpService } from './_angular/service/lemon-http.service'
import { DragAndDropService } from './_jaettu-angular/service/drag-and-drop.service'
import { LemonTranslationService } from './_jaettu-angular/service/lemon-translation.service'
import { VasenValikkoService } from './_jaettu-angular/service/vasen-valikko.service'
import { WindowSizeService } from './_jaettu-angular/service/window.service'

import { Asiakas, Kayttaja, KnowYourCustomer, LemonaidAsiakkaanKuukausihinta, NewFeature, SopimusPalvelupaketti, Yritysmuoto } from '@lemon/core-firebase/model/kayttaja'
import { Laskuasetukset } from '@lemon/core-firebase/model/lasku'

import { BehaviorSubject, combineLatest, Observable, of as observableOf, Subject } from 'rxjs'
import { catchError, delay, distinctUntilChanged, filter, map, pairwise, startWith, switchMap, take, takeUntil, tap } from 'rxjs/operators'

import { environment } from 'environments/environment'
import { EnvironmentType } from './app.environment'
import { AiiaDialogData, ViiaLoginRequiredDialog } from './banks/viia/viia-login-required.dialog'
// import { ViiaAsiakkaanAutentikointitiedot, ViiaHaeIbanitLemonaidResponse } from '@lemon/core-firebase/model/aiia'
import { BankCustomerConsent, BankDialogType } from '@lemon/core-firebase/model/banks'
import { LemonaidJulkaistuViesti } from '@lemon/core-firebase/model/lemonaid-viesti'
import { AsiakasJaettuLemonaidService } from '@lemon/core-firebase/service/asiakas-jaettu-lemonaid.service'
import { BankConsentUriService } from '@lemon/core-firebase/service/bank-consent-uri.service'
import { KycUriService } from '@lemon/core-firebase/service/kyc-uri.service'
import { LemonaidViestiUriService } from '@lemon/core-firebase/service/viestit/lemonaid-viesti-uri.service'
import { DateService } from '@lemon/core/service/date.service'
import { AllowedDialogsService } from './_angular/service/allowed-dialogs.service'
import { EnableBankingDialogService } from './_angular/service/enable-banking-dialog.service'
import { FirebaseLemonaid } from './_angular/service/firebase-lemonaid.service'
import { HolviDialogService } from './_angular/service/holvi-dialog.service'
import { MuutoksiaPalveluunDialogService } from './_angular/service/muutoksia-palveluun-dialog.service'
import { TilinpaatosLemonaidUsersService } from './_angular/service/tilinpaatos-lemonaid-users.service'
import { TunnistautuminenService } from './_angular/service/tunnistautuminen.service'
import { TuntemistiedotDialogService } from './_angular/service/tuntemistiedot-dialog.service'
import { UserSettingsService } from './_angular/service/user-settings.service'
import { AsiakasKyselyDialog, AsiakasKyselyDialogData } from './asetukset/asiakaskysely.dialog'
import { EnableBankingDialogData, EnableBankingLoginRequiredDialog } from './banks/enable-banking/enable-banking-login-required.dialog'
import { GeneralBankSelectionDialog, GeneralBankSelectionDialogData } from './banks/general-bank-selection.dialog'
import { HolviLoginRequiredDialog, HolviLoginRequiredDialogData } from './banks/holvi/holvi-login-required.dialog'
import { MuutoksiaPalveluunDialog, MuutoksiaPalveluunDialogData } from './sopimukset/muutoksia-palveluun.dialog'
import { TervetuloaSopimusV2Dialog, TervetuloaSopimusV2DialogData } from './tervetuloa/tervetuloa-sopimus-v2.dialog'
import { UudetTuntemistiedotDialog, UudetTuntemistiedotDialogData } from './tervetuloa/uudet-tuntemistiedot.dialog'
import { TunnistautuminenNetsDialog, TunnistautuminenNetsDialogData } from './tunnistautuminen/nets/tunnistautuminen-nets.dialog'

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  encapsulation: ViewEncapsulation.None,
  animations: [
    trigger('sisaltoAuki', [
      state('vasen', style({ 'margin-left': '0' })),

      state('oikea', style({ 'margin-left': '220px' })),
      transition('* <=> *', animate('400ms 0ms cubic-bezier(0.25, 0.8, 0.25, 1)')),
    ]),
    trigger('valikkoAuki', [
      state('vasen, vasenpika', style({ transform: 'translate3d(-100%, 0, 0)', 'box-shadow': 'none', display: 'none;' })),

      state('oikea', style({ transform: 'none', 'box-shadow': '0px 8px 10px -5px rgba(0, 0, 0, 0.2), 0px 16px 24px 2px rgba(0, 0, 0, 0.14), 0px 6px 30px 5px rgba(0, 0, 0, 0.12)' })),
      transition('vasen <=> oikea', animate('400ms 0ms cubic-bezier(0.25, 0.8, 0.25, 1)')),
      transition('vasenpika <=> *', animate('0ms')),
      transition('* <=> vasenpika', animate('0ms')),
    ]),
    trigger('tummaTausta', [
      state('poissa', style({ 'background-color': 'transparent' })),

      state('nakyvissa', style({ 'background-color': 'rgba(0, 0, 0, 0.6)' })),
      transition('* <=> *', animate('400ms 0ms cubic-bezier(.25,.8,.25,1)')),
    ]),
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  @ViewChild('outletti', { read: RouterOutlet, static: true }) routerOutlet: RouterOutlet
  @ViewChild('nav', { read: MatSidenav }) nav: MatSidenav

  private avattuNappulasta: BehaviorSubject<boolean> = new BehaviorSubject(false)

  urlObservable: Observable<string>
  kirjautunutKayttajaObservable: Observable<Kayttaja>
  naytaImpersonointiBanneriObservable: Observable<boolean>
  naytettavaViestiObservable: Observable<string>
  naytaTyokieltoBanneriObservable: Observable<boolean>
  asetuksetObservable: Observable<Laskuasetukset>

  private _vasenValikkoAukiObservable: Observable<boolean>
  private _onkoOverTilaObservable: Observable<boolean>
  vasemmanValikonTilaObservable: Observable<'side' | 'over'>
  onkoSisaltoKapeaObservable: Observable<boolean>
  vasemmanValikonAnimointiTila: Observable<'vasenpika' | 'vasen' | 'oikea'>
  sisallonAnimointitilaObservable: Observable<'vasenpika' | 'vasen' | 'oikea'>
  onkoValikonVerhoNakyvissaObservable: Observable<boolean>
  valikonAvaajaNakyvissaObservable: Observable<boolean>

  showNewFeatureSignAtMaksutLaskujenVastaanottoObservable: Observable<boolean>
  showAdvertiseObservable: Observable<boolean>
  combinedObservable: Observable<boolean>

  private _ngUnsubscribe: Subject<void> = new Subject<void>()

  private _vasemmanValikonVaihtuminen = 801
  private _initiatedInsideDocument = false

  naytaBeta = environment.environment === EnvironmentType.BETA
  naytaInternetYhteysOngelmaObservable: Observable<boolean>

  constructor(
    private _applicationRef: ApplicationRef,
    private _errorHandler: ErrorHandler,
    private _kayttajaService: KayttajaService,
    private _router: Router,
    private _dialog: MatDialog,
    private _vasenValikkoService: VasenValikkoService,
    private _laskuService: LaskuService,
    private _windowSizeService: WindowSizeService,
    private _translationService: LemonTranslationService,
    private _http: LemonHttpService,
    private _dndService: DragAndDropService,
    private _renderer: Renderer2,
    private _allowedDialogsService: AllowedDialogsService,
    private _aiiaDialogService: AiiaDialogService,
    private _holviDialogService: HolviDialogService,
    private _enableBankingDialogService: EnableBankingDialogService,
    private _dateService: DateService,
    private _tunnistautuminenService: TunnistautuminenService,
    private _kycUriService: KycUriService,
    private _tuntemistiedotDialogService: TuntemistiedotDialogService,
    private _muutoksiaPalveluunDialogService: MuutoksiaPalveluunDialogService,
    private _tilinpaatosLemonaidUsersService: TilinpaatosLemonaidUsersService,
    private _firebaseLemonaid: FirebaseLemonaid,
    private _bankConsentUriService: BankConsentUriService,
    private _userSettingsService: UserSettingsService,
    private _asiakasJaettuService: AsiakasJaettuLemonaidService,
  ) {
    this.vasemmanValikonTilaObservable = this._windowSizeService.sizeObservable.pipe(
      map(() => {
        const leveys = window.innerWidth || document.documentElement.clientWidth || document.getElementsByTagName('body')[0].clientWidth
        return leveys <= this._vasemmanValikonVaihtuminen ? 'over' : 'side'
      }),
      distinctUntilChanged<'over' | 'side'>(),
    )

    this._vasenValikkoAukiObservable = combineLatest([
      this._vasenValikkoService.aukiObservable,
      this._kayttajaService.kayttajanTiedotObservable,
      this.vasemmanValikonTilaObservable,
      this.avattuNappulasta,
    ]).pipe(
      map(([servicenPuolestaAuki, kayttajanTiedot, leveydenPuolestaAuki, avattuNappulasta]) => {
        const kayttajanPuolestaAuki = !!kayttajanTiedot

        if (avattuNappulasta) {
          return true
        }

        // console.log('Vasen valikko auki')
        return kayttajanPuolestaAuki && servicenPuolestaAuki && leveydenPuolestaAuki === 'side'
      }),
    )

    this.onkoSisaltoKapeaObservable = combineLatest([this.vasemmanValikonTilaObservable, this._vasenValikkoAukiObservable]).pipe(
      map(([valikonTila, valikkoOnAuki]) => {
        // console.log('Vasen valikko auki')
        return valikonTila === 'over' || (valikonTila === 'side' && !valikkoOnAuki)
      }),
    )

    // const kayttoehtojenHyvaksynnatObservable: Observable<KayttoehtojenHyvaksynnat> = kayttajaService.kayttajanTiedotObservable.pipe(
    //   switchMap(kayttajanTiedot => {
    //     if (kayttajanTiedot) {
    //       return this.lemonaidDatabase.object(this.kayttoehdotUriService.getKayttoehdotRealtimeApprovalsCollectionUri(kayttajanTiedot.uid)).valueChanges().pipe(
    //         map(approvals => {
    //           if (approvals) {
    //             return approvals as KayttoehtojenHyvaksynnat
    //           }
    //         })
    //       )
    //     }
    //     return observableOf<KayttoehtojenHyvaksynnat>(null)
    //   }),
    // )
    // const nykyistenKayttoehtojenAvainObservable: Observable<string> = kayttajaService.kayttajanTiedotObservable.pipe(
    //   switchMap(kayttajanTiedot => {
    //       if (kayttajanTiedot) {
    //         return this.lemonaidDatabase.object(this.kayttoehdotUriService.getKayttoehdotRealtimeLatestKeyUri()).valueChanges().pipe(
    //           map(data => {
    //             return data as string}))
    //         }
    //       return observableOf<string>(null)
    //   })
    // )
    // combineLatest(kayttoehtojenHyvaksynnatObservable,
    //               nykyistenKayttoehtojenAvainObservable,
    //               kayttajaService.kayttajanTiedotObservable,
    //               translationService.currentLanguageObservable)
    //               .pipe(
    //   switchMap(([hyvaksynnat, kayttoehtojenAvain, kayttajaTiedot, valittuKieli]) => {
    //     if (!kayttoehtojenAvain) {
    //       return observableOf<KayttoehtojenDialogData>(null)
    //     }
    //     const findApproval = hyvaksynnat ? hyvaksynnat[kayttoehtojenAvain] : null
    //     if (!findApproval || !hyvaksynnat) {
    //       return this.lemonaidDatabase.object(this.kayttoehdotUriService.getKayttoehdotRealtimeLatestTextUri(valittuKieli)).valueChanges().pipe(
    //         map(async text => {
    //           const kayttoehtojenText: string = text as string
    //           const kayttoehtojenDialogData: KayttoehtojenDialogData = {
    //             uid: kayttajaTiedot.uid,
    //             kayttoehtojenAvain: kayttoehtojenAvain,
    //             text: kayttoehtojenText.replace(kayttoehtojenAvain, '')
    //           }
    //           return kayttoehtojenDialogData
    //         })
    //       )
    //     }
    //     return observableOf<KayttoehtojenDialogData>(null)
    //   }),
    //   takeUntil(this._ngUnsubscribe)
    // ).subscribe(async data => {
    //   // Sulje dialogi, jos sellainen oli
    //   if ( this.uudetKayttoehdotDialogi ) {
    //     this.uudetKayttoehdotDialogi.close()
    //     this.uudetKayttoehdotDialogi = null
    //   }
    //   const output: KayttoehtojenDialogData = await data
    //   if (output) {
    //     this.naytaUudetKayttoehdotDialogi(output)
    //   }
    // })

    const alkuperainenTila = this._vasenValikkoService.annaAlkuperainenTila(this._vasemmanValikonVaihtuminen)
    this.sisallonAnimointitilaObservable = this.onkoSisaltoKapeaObservable.pipe(
      map(kapea => {
        return kapea ? 'vasen' : 'oikea'
      }),
      startWith<'vasen' | 'oikea' | 'vasenpika'>(alkuperainenTila),
    )

    this.vasemmanValikonAnimointiTila = this._vasenValikkoAukiObservable.pipe(
      map(auki => {
        return auki ? 'oikea' : 'vasen'
      }),
      startWith<'vasen' | 'oikea' | 'vasenpika'>(alkuperainenTila === 'vasen' ? 'vasenpika' : 'oikea'),
    )

    this.urlObservable = this._router.events.pipe(
      filter(event => {
        return event instanceof NavigationStart
      }),
      map((event: NavigationStart) => {
        return event.url
      }),
      distinctUntilChanged(),
      delay(1),
      takeUntil(this._ngUnsubscribe),
    )
    this.kirjautunutKayttajaObservable = this._kayttajaService.kayttajaObservable
    // Banneri näytetään vain kun käyttäjä on kirjautunut JA impersonointi on käynnissä.
    // KayttajaService palauttaa turvasyistä impersonoidaan=true myös kun käyttäjätietoja ei ole, joten erillinen tarkistus tarvitaan.
    this.naytaImpersonointiBanneriObservable = combineLatest([this.kirjautunutKayttajaObservable, this._kayttajaService.kayttajaaImpersonoidaanObservable]).pipe(
      map(([kayttaja, impersonoidaan]) => !!kayttaja && impersonoidaan),
    )

    // Kirjautuneille ja kirjautumattomille on omat viestidokumenttinsa, joista näytetään
    // kirjautumistilan mukaan aina vain toinen. Viestivirran virhe ei saa kaataa banneria.
    const lemonaidViestiUriService = new LemonaidViestiUriService()
    this.naytettavaViestiObservable = this.kirjautunutKayttajaObservable.pipe(
      switchMap(kayttaja =>
        this._firebaseLemonaid
          .firestoreDoc<LemonaidJulkaistuViesti>(lemonaidViestiUriService.annaJulkaistunViestinUri(kayttaja ? 'kirjautunut' : 'julkinen'))
          .listen()
          .pipe(
            catchError(err => {
              console.error('Lemonaid-viestin kuuntelu epäonnistui', err)
              return observableOf<LemonaidJulkaistuViesti>(null)
            }),
          ),
      ),
      map(viesti => viesti?.viesti || null),
      distinctUntilChanged(),
    )

    this.naytaTyokieltoBanneriObservable = this._kayttajaService.nykyinenAsiakasObservable.pipe(map(asiakas => asiakas?.tyokieltoAlku && asiakas.tyokieltoAlku.toMillis() <= Date.now()))

    this.asetuksetObservable = this._laskuService.asetuksetObservable

    this._renderer.listen('document', 'dragstart', () => {
      // console.log('RENDERER START DRAG')
      this._initiatedInsideDocument = true
    })

    this._renderer.listen('document', 'dragend', () => {
      // console.log('RENDERER END DRAG')
      this._initiatedInsideDocument = false
    })

    this._renderer.listen('document', 'drop', () => {
      // console.log('RENDERER DROP')
      this._initiatedInsideDocument = false
      this._dndService.setDragging(false)
    })

    this._renderer.listen('document', 'dragover', () => {
      // console.log('RENDERER DRAGOVER')
      if (!this._initiatedInsideDocument) {
        this._dndService.setDragging(true)
      }
    })

    this._renderer.listen('document', 'dragenter', () => {
      // console.log('RENDERER DRAGENTER')
      if (!this._initiatedInsideDocument) {
        this._dndService.setDragging(true)
      }
    })

    this._renderer.listen('document', 'dragleave', () => {
      // console.log('RENDERER DRAG LEAVE')
      this._dndService.setDragging(false)
    })

    this._onkoOverTilaObservable = this.vasemmanValikonTilaObservable.pipe(
      map(tila => {
        // console.log('Overtila')
        return tila === 'over'
      }),
    )

    this.onkoValikonVerhoNakyvissaObservable = combineLatest([this._onkoOverTilaObservable, this._vasenValikkoAukiObservable]).pipe(
      map(([overTila, valikkoAuki]) => {
        // console.log('Vasen valikko onkoValikonVerhoNakyvissaObservable')
        return overTila && valikkoAuki
      }),
      tap(nakyvissa => {
        if (nakyvissa) {
          this._renderer.addClass(document.body, 'noscroll')
          this._renderer.addClass(document.documentElement, 'noscroll')
        } else {
          this._renderer.removeClass(document.body, 'noscroll')
          this._renderer.removeClass(document.documentElement, 'noscroll')
        }
      }),
    )

    this.valikonAvaajaNakyvissaObservable = combineLatest([this._onkoOverTilaObservable, this._vasenValikkoService.aukiObservable, this.kirjautunutKayttajaObservable]).pipe(
      map(([overTila, valikkoAuki, kirjautunutKayttaja]) => {
        return overTila && valikkoAuki && !!kirjautunutKayttaja
      }),
      delay(10),
    )

    // const isAiiaLoginRequiredObservable = combineLatest([this._kayttajaService.kayttajaObservable, this._kayttajaService.nykyinenAsiakasObservable]).pipe(
    //   switchMap(([kayttaja, asiakas]) => {

    //     if (this._router.url.startsWith('/ajopaivakirja')) {
    //       return observableOf<boolean>(false)
    //     }

    //     if (this._aiiaDialogService.aiiaFlowCompletedRecently()) {
    //       return observableOf<boolean>(false)
    //     }

    //     if (!asiakas || !kayttaja || kayttaja.roolit.HALLINTO_VIIA_VALTUUTUKSEN_ANTAJA !== true) {
    //       return observableOf<boolean>(false)
    //     }
    //     // Has the customer been marked as needing a Viia login?
    //     return this._isViiaLoginRequired(asiakas)

    //   }),
    //   switchMap(isRequired => {
    //     if (!isRequired) {
    //       return observableOf<ViiaHaeIbanitLemonaidResponse>(null)
    //     }
    //     // Create observable that fetches the ibans we have access to.
    //     return this._firebaseLemonaid.functionsCall<string, ViiaHaeIbanitLemonaidResponse>('viiaGetUserIbansLemonaidUusiProd', '').then(ibanResponse => {
    //       if (!ibanResponse) { return null }
    //       if (ibanResponse.e) {
    //         this._errorHandler.handleError(new Error('Error while fetching viia ibans: ' + ibanResponse.e))
    //         return null
    //       }
    //       return ibanResponse
    //     })
    //   }),
    //   map(ibanResponse => {
    //     if (ibanResponse?.ibanitJaProviderIdt) {
    //       const aiiaDialogConfig: MatDialogConfig<AiiaDialogData> = {
    //         data: {
    //           // ibanitJaProviderIdt: ibanResponse.ibanitJaProviderIdt, // Will be deprecated
    //           dialogToShowMetadata: null // New field, replaces ibanitJaProviderIdt
    //         }
    //       }
    //       return aiiaDialogConfig
    //     }
    //     return null
    //   })
    // )

    this.kirjautunutKayttajaObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(kayttaja => {
      if (!kayttaja) {
        return
      }
      // Kirjautumissivulla vaihdettu kieli voittaa tallennetun asiointikielen ja päivitetään palvelimelle
      const kirjautumattomanValinta = this._translationService.otaKirjautumattomanValinta()
      if (kirjautumattomanValinta) {
        if (kayttaja.asiointikieli !== kirjautumattomanValinta) {
          this._kayttajaService.tallennaKayttajanKieli(kirjautumattomanValinta).catch(err => this._errorHandler.handleError(err))
        }
        return
      }
      if (kayttaja.asiointikieli) {
        this._translationService.vaihdaKieli(kayttaja.asiointikieli)
      }
    })

    combineLatest([this._kayttajaService.kayttajanTiedotObservable, this.kirjautunutKayttajaObservable])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([tiedot, kayttaja]) => {
        if (tiedot && kayttaja && environment.environment === EnvironmentType.BETA) {
          if (!tiedot.euid && !kayttaja.email.endsWith('@lemontree.fi')) {
            this._kayttajaService.logout()
          }
        }
      })

    this.naytaInternetYhteysOngelmaObservable = this._http.internetYhteysObservable.pipe(
      map(online => {
        return !online
      }),
    )

    const naytaTuntemistiedotDialogObservable = combineLatest([this._kayttajaService.nykyinenAsiakasObservable, this._kayttajaService.kayttajaObservable]).pipe(
      map(([asiakas, kayttaja]) => {
        if (asiakas && kayttaja) {
          if (this._tuntemistiedotDialogService.postponedRecently()) {
            return null
          }
          if (this._naytaTuntemistiedotDialogi(asiakas, kayttaja)) {
            return asiakas
          }
        }
        return null
      }),
      switchMap(asiakas => {
        if (asiakas) {
          return this._firebaseLemonaid
            .firestoreDoc<KnowYourCustomer>(this._kycUriService.getCustomerKycDocUri(asiakas.asiakasAvain))
            .listen()
            .pipe(
              map(kyc => {
                // Tarkistetaan lykkäys myös tässä, koska Firestore-kuuntelija emittoi
                // suoraan switchMap:n kautta ohittaen ylemmän map:n tarkistuksen
                if (this._tuntemistiedotDialogService.postponedRecently()) {
                  return null
                }
                const config: MatDialogConfig<UudetTuntemistiedotDialogData> = {
                  data: {
                    kyc: kyc || null,
                  },
                }
                return config
              }),
            )
        }
        return observableOf<MatDialogConfig<UudetTuntemistiedotDialogData>>(null)
      }),
    )

    const naytaTervetuloaDialogObservable = combineLatest([this._kayttajaService.nykyinenAsiakasObservable, this._kayttajaService.kayttajaObservable]).pipe(
      switchMap(([asiakas, kayttaja]) => {
        const naytaTuntemistiedot = this._naytaTuntemistiedotDialogi(asiakas, kayttaja)
        const naytaTunnistaminen = this._naytaTunnistamisDialog(kayttaja)
        const naytaSopimus = this._naytaSopimusDialog(kayttaja)

        if (!asiakas?.sopimuskaudet?.length || !kayttaja || kayttaja.tervetuloaDialogiNaytetty || !naytaSopimus) {
          return observableOf<MatDialogConfig<TervetuloaSopimusV2DialogData>>(null)
        }

        const nykyinenTaiVoimaanTulevaSopimuskausi = this._asiakasJaettuService.annaNykyinenTaiVoimaanTulevaSopimuskausi(asiakas.sopimuskaudet)

        if (!nykyinenTaiVoimaanTulevaSopimuskausi) {
          this._errorHandler.handleError(new Error('Ei sopimuskautta päivällä ' + this._dateService.currentNumberDate()))
          return observableOf<MatDialogConfig<TervetuloaSopimusV2DialogData>>(null)
        }
        if (!nykyinenTaiVoimaanTulevaSopimuskausi.sopimusversioAvain || !nykyinenTaiVoimaanTulevaSopimuskausi.palvelupaketti) {
          this._errorHandler.handleError(
            new Error(
              'Ei uutta sopimusversiota tai palvelupakettia sopimuskaudella ' +
                this._dateService.localDateToNumber(nykyinenTaiVoimaanTulevaSopimuskausi.alkaa) +
                '-' +
                this._dateService.localDateToNumber(nykyinenTaiVoimaanTulevaSopimuskausi.loppuu),
            ),
          )
          return observableOf<MatDialogConfig<TervetuloaSopimusV2DialogData>>(null)
        }

        if (nykyinenTaiVoimaanTulevaSopimuskausi.palvelupaketti === SopimusPalvelupaketti.HOLVI_ZEN) {
          // Holvi Zen -asiakkaille ei näytetä tervetuloa-sopimusdialogia, koska heillä ei ole Lemonaid-sopimusta.
          return observableOf<MatDialogConfig<TervetuloaSopimusV2DialogData>>(null)
        }

        return combineLatest([this._kayttajaService.nykyisenAsiakkaanHinnatObservable, this._kayttajaService.muutJatkuvatVeloituksetObservable]).pipe(
          filter(([hinnat, muutJatkuvatVeloitukset]) => !!hinnat?.length || !!muutJatkuvatVeloitukset?.length),
          take(1),
          map(([hinnat, muutJatkuvatVeloitukset]) => {
            const { nykyinen, seuraava } = this._asiakasJaettuService.annaNykyinenJaSeuraavaLemonaidHinta(hinnat)
            const { nykyiset, tulevat } = this._asiakasJaettuService.annaNykyisetJaVoimaanTulevatMuutJatkuvatVeloitukset(muutJatkuvatVeloitukset, this._dateService.currentLocalDate())

            let hinta: LemonaidAsiakkaanKuukausihinta = null
            if (nykyinen?.hinta) {
              hinta = nykyinen
            } else if (seuraava?.hinta) {
              hinta = seuraava
            }

            if (!hinta && !nykyiset?.length && !tulevat?.length) {
              this._errorHandler.handleError(new Error('Ei hintaa eikä muita jatkuvia veloituksia päivällä ' + this._dateService.currentNumberDate()))
              return null
            }

            const kaytettavaLeveys = window.innerWidth
            const kaytettavaKorkeus = window.innerHeight
            const leveys = kaytettavaLeveys > 900 ? '90vw' : '98vw'

            const settings: MatDialogConfig<TervetuloaSopimusV2DialogData> = {
              maxWidth: '1000px',
              width: leveys,
              // maxHeight: '800px',
              disableClose: true,
              data: {
                naytaTunnistaminen: naytaTunnistaminen,
                naytaSopimus: naytaSopimus,
                naytaTuntemistiedot: naytaTuntemistiedot,
                hinta: hinta,
                mobiili: kaytettavaLeveys < 670 || kaytettavaKorkeus < 670,
                kayttajaJoTunnistettu: kayttaja.kayttajaTunnistettu === 'tunnistettu-nets',
                pepMissing: !kayttaja.pepTiedotAnnettu,
                sopimuskausi: nykyinenTaiVoimaanTulevaSopimuskausi,
                muutJatkuvatVeloitukset: [...nykyiset, ...tulevat],
              },
            }
            return settings
          }),
        )
      }),
    )

    const naytaNetsTunnistautuminenDialogObservable = this._kayttajaService.kayttajaObservable.pipe(
      map(kayttaja => {
        if (this._naytaTunnistamisDialog(kayttaja)) {
          const settings: MatDialogConfig<TunnistautuminenNetsDialogData> = {
            data: {
              kayttajaJoTunnistettu: kayttaja.kayttajaTunnistettu === 'tunnistettu-nets',
              pepMissing: !kayttaja.pepTiedotAnnettu,
            },
          }
          return settings
        }
        return null
      }),
    )

    const naytaMuutoksiaPalveluunDialog = combineLatest([this._kayttajaService.nykyinenAsiakasObservable, this._kayttajaService.kayttajaObservable]).pipe(
      map(([asiakas, kayttaja]) => {
        if (!asiakas?.sopimuskaudet?.length || !this._naytaSopimusDialog(kayttaja) || this._muutoksiaPalveluunDialogService.postponedRecently() || !kayttaja.tervetuloaDialogiNaytetty) {
          return null
        }
        const nykyinenTaiVoimaanTulevaSopimuskausi = this._asiakasJaettuService.annaNykyinenTaiVoimaanTulevaSopimuskausi(asiakas.sopimuskaudet)

        if (!nykyinenTaiVoimaanTulevaSopimuskausi) {
          this._errorHandler.handleError(new Error('Ei sopimuskautta päivällä ' + this._dateService.currentNumberDate()))
          return null
        }
        if (!nykyinenTaiVoimaanTulevaSopimuskausi.sopimusversioAvain || !nykyinenTaiVoimaanTulevaSopimuskausi.palvelupaketti) {
          this._errorHandler.handleError(
            new Error(
              'Ei uutta sopimusversiota tai palvelupakettia sopimuskaudella ' +
                this._dateService.localDateToNumber(nykyinenTaiVoimaanTulevaSopimuskausi.alkaa) +
                '-' +
                this._dateService.localDateToNumber(nykyinenTaiVoimaanTulevaSopimuskausi.loppuu),
            ),
          )
          return null
        }

        if (nykyinenTaiVoimaanTulevaSopimuskausi.palvelupaketti === SopimusPalvelupaketti.HOLVI_ZEN) {
          // Holvi Zen -asiakkaille ei näytetä MuutoksiaPalveluun-dialogia, koska heillä ei ole Lemonaid-sopimusta.
          return null
        }

        const kaytettavaLeveys = window.innerWidth
        const leveys = kaytettavaLeveys > 900 ? '90vw' : '98vw'
        const settings: MatDialogConfig<MuutoksiaPalveluunDialogData> = {
          maxWidth: '1000px',
          width: leveys,
          // maxHeight: '800px',
          disableClose: true,
          data: {
            vanhaVersio: nykyinenTaiVoimaanTulevaSopimuskausi?.edellinen || null,
            uusiVersio: nykyinenTaiVoimaanTulevaSopimuskausi,
          },
        }
        return settings
      }),
    )

    // const naytaSopimusDialogObservable = this._kayttajaService.kayttajaObservable.pipe(
    //   switchMap(kayttaja => {

    //     if (!this._naytaSopimusDialog(kayttaja) || this._uusiSopimusDialogService.postponedRecently()) {
    //       return observableOf(null)
    //     }
    //     const lang = this._translationService.nykyinenKieli
    //     const reqData: SopimuksenHintaJaTekstiRequest = {
    //       kieli: lang || 'fi'
    //     }

    //     const hintaJaTekstiPromise: Promise<SopimuksenHintaJaTekstiResponse> = this._firebaseLemonaid.functionsCall<SopimuksenHintaJaTekstiRequest, SopimuksenHintaJaTekstiResponse>('sopimusAnnaKayttajanTekstiJaHinta', reqData)
    //       .then(resp => {
    //         if (!resp || (resp && resp.e)) {
    //           this._errorHandler.handleError(new Error('Sopimus text and price fetch failed!' + (resp?.e ?? 'unknown-error')))
    //           return null
    //         }
    //         return resp
    //       }).catch(err => {
    //         this._errorHandler.handleError(err)
    //         return null
    //       })

    //     return from(hintaJaTekstiPromise).pipe(
    //       map((lastPriceAndSopimus: SopimuksenHintaJaTekstiResponse) => {
    //         // If no data from CF, stop
    //         if (!lastPriceAndSopimus) {
    //           return null
    //         }
    //         const kaytettavaLeveys = window.innerWidth
    //         // const kaytettavaKorkeus = window.innerHeight
    //         const leveys = kaytettavaLeveys > 900 ? '90vw' : '98vw'
    //         // const korkeus = kaytettavaKorkeus > 1100 ? '80vh' : kaytettavaKorkeus > 768 ? '85vh' : '95vh'

    //         const settings: MatDialogConfig<UusiSopimusDialogData> = {
    //           maxWidth: '1000px',
    //           width: leveys,
    //           // maxHeight: '800px',
    //           disableClose: true,
    //           data: {
    //             sopimuksenAvain: lastPriceAndSopimus.sopimusAvain,
    //             sopimusPaattyy: this._dateService.lisaaPaiviaPaikallinen(this._dateService.currentLocalDate(), 14),
    //             kuukausiHinta: lastPriceAndSopimus.hinta?.hinta,
    //             hintaMuuttuu: false,
    //             kieli: lang
    //           }
    //         }
    //         return settings
    //       })
    //     )
    //   })
    // )

    const _bankCustomerConsentObservable: Observable<BankCustomerConsent> = this._kayttajaService.kayttajaObservable.pipe(
      switchMap(kayttaja => {
        if (!kayttaja) {
          return observableOf<BankCustomerConsent>(null)
        }
        if (!kayttaja.roolit['HALLINTO_VIIA_VALTUUTUKSEN_ANTAJA']) {
          return observableOf<BankCustomerConsent>(null)
        }
        return this._firebaseLemonaid.firestoreDoc<BankCustomerConsent>(this._bankConsentUriService.getBankConsentUri(kayttaja.asiakasAvain)).listen()
      }),
    )

    const showGeneralBankSelectionDialogObservable: Observable<MatDialogConfig<GeneralBankSelectionDialogData>> = _bankCustomerConsentObservable.pipe(
      map(consent => {
        if (!this._shouldShowBankDialog(consent, [BankDialogType.GENERAL])) {
          // Note: GeneralBankSelectionDialog does not have postpone option
          return null
        }

        const settings: MatDialogConfig<GeneralBankSelectionDialogData> = {
          maxWidth: '1000px',
          // maxHeight: '800px',
          disableClose: true,
          data: {},
        }
        return settings
      }),
    )

    // const showGeneralWithoutHolviConnectionDialogObservable: Observable<MatDialogConfig<GeneralBankSelectionDialogData>> = _bankCustomerConsentObservable.pipe(
    //   map(consent => {
    //     if (!this._shouldShowBankDialog(consent, BankDialogType.GENERAL_WO_HOLVI)) {
    //       return null
    //     }

    //     const settings: MatDialogConfig<GeneralBankSelectionDialogData> = {
    //       maxWidth: '1000px',
    //       // maxHeight: '800px',
    //       disableClose: true,
    //       data: {
    //         includeHolvi: false
    //       }
    //     }
    //     return settings
    //   })
    // )

    const showHolviConnectionDialogObservable: Observable<MatDialogConfig<HolviLoginRequiredDialogData>> = _bankCustomerConsentObservable.pipe(
      map(consent => {
        if (!this._shouldShowBankDialog(consent, [BankDialogType.HOLVI_RENEW], this._holviDialogService)) {
          return null
        }

        const settings: MatDialogConfig<HolviLoginRequiredDialogData> = {
          maxWidth: '1000px',
          disableClose: true,
          data: {},
        }
        return settings
      }),
    )

    const showAiiaConnectionDialogObservable: Observable<MatDialogConfig<AiiaDialogData>> = _bankCustomerConsentObservable.pipe(
      map(consent => {
        if (!this._shouldShowBankDialog(consent, [BankDialogType.PSD2_RENEW, BankDialogType.GENERAL_WO_HOLVI], this._aiiaDialogService)) {
          return null
        }

        const psd2Dialog = consent.dialogsToShow?.find(dialog => dialog.dialogToShow === BankDialogType.PSD2_RENEW)
        if (!psd2Dialog) {
          return null
        }

        const settings: MatDialogConfig<AiiaDialogData> = {
          data: {
            // ibanitJaProviderIdt: null,
            dialogToShowMetadata: psd2Dialog ?? null,
          },
        }
        return settings
      }),
    )

    const showEnableBankingDialogObservable: Observable<MatDialogConfig<EnableBankingDialogData>> = _bankCustomerConsentObservable.pipe(
      switchMap(async consent => {
        if (!this._shouldShowBankDialog(consent, [BankDialogType.ENABLE_BANKING], this._enableBankingDialogService)) {
          return null
        }

        const enableBankingDialog = consent.dialogsToShow.find(dialog => dialog.dialogToShow === BankDialogType.ENABLE_BANKING && !!dialog.ibansMissing?.length)

        if (!enableBankingDialog) {
          return null
        }
        // Esiladataan ASPSP-lista ennen dialogin avaamista, jotta picker-chipit renderöityvät synkronisesti.
        const bankSubtypes = await this._enableBankingDialogService.getAspspDisplayData()

        // Reconnect-flow lukee (name, psuType) suoraan providerMetadata.enableBankingMetadata:sta,
        // joten picker-listan puuttuminen ei saa tukahduttaa uudelleenvaltuutuskehotusta.
        const ebMetadata = enableBankingDialog.providerMetadata?.enableBankingMetadata
        const hasReconnectMetadata = !!ebMetadata?.aspsp?.name && !!ebMetadata?.psuType
        if (!hasReconnectMetadata) {
          const bankTypesForIban = this._enableBankingDialogService.getBankSubtypesForIban(enableBankingDialog.ibansMissing[0], bankSubtypes)
          if (!bankTypesForIban?.length) {
            this._errorHandler.handleError(new Error('No bank types found for IBAN ' + enableBankingDialog.ibansMissing[0]))
            return null
          }
        }

        const settings: MatDialogConfig<EnableBankingDialogData> = {
          data: {
            dialogToShowMetadata: enableBankingDialog ?? null,
            bankSubtypes,
          },
        }
        return settings
      }),
    )

    // Pop-up mainos
    // const naytaLaskujenVastaanottoMainosDialogObservable: Observable<MatDialogConfig<MaksutMainosLaskujenVastaanottoDialogData>> = this._kayttajaService.kayttajaObservable.pipe(
    //   switchMap(kayttaja => {

    //     if (!kayttaja?.avain ||
    //       !kayttaja?.asiakasAvain ||
    //       !kayttaja.roolit ||
    //       !kayttaja.roolit[Roolit.MAKSUT_VASTAANOTTAJA]
    //     ) {
    //       return observableOf<MatDialogConfig<MaksutMainosLaskujenVastaanottoDialogData>>(null)
    //     }

    //     return this._userSettingsService.userSettingsObservable.pipe(
    //       switchMap(userSettings => {

    //         if (userSettings?.seen?.[NewFeature.MAKSUT_LASKUJEN_VASTAANOTTO_MAINOS]) {
    //           return observableOf<MatDialogConfig<MaksutMainosLaskujenVastaanottoDialogData>>(null)
    //         }

    //         return this._firebaseLemonaid.firestoreDoc<ApixReceivedInvoiceConfig>('customers/' + kayttaja.asiakasAvain + '/apix-received-invoice-config/' + kayttaja.asiakasAvain).listen().pipe(
    //           map(config => {
    //             const vastaanottoActive = !!config && (config.receiveActive || config.forwardActive || config.paymentReceiveIsActive)
    //             if (vastaanottoActive) {
    //               return null
    //             }
    //             const dialogData: MaksutMainosLaskujenVastaanottoDialogData = {
    //               kayttajaUid: kayttaja.avain,
    //               kayttajaAsiakasAvain: kayttaja.asiakasAvain,
    //             }
    //             const dialogConfig: MatDialogConfig<MaksutMainosLaskujenVastaanottoDialogData> = {
    //               data: dialogData,
    //               autoFocus: false
    //             }

    //             return dialogConfig
    //           })
    //         )

    //       })
    //     )

    //   })
    // )

    const naytaAsiakasKyselyDialogObservable: Observable<MatDialogConfig<AsiakasKyselyDialogData> | null> = this._kayttajaService.kayttajanTiedotObservable.pipe(
      switchMap(kayttajanTiedot => {
        if (!kayttajanTiedot?.uid || !kayttajanTiedot?.asiakasAvain) {
          return observableOf<MatDialogConfig<AsiakasKyselyDialogData> | null>(null)
        }

        return this._userSettingsService.userSettingsObservable.pipe(
          filter(userSettings => userSettings !== null),
          map(userSettings => {
            if (userSettings?.shownUntil?.[NewFeature.ASIAKASKYSELY]) {
              return null
            }
            const voimassaAstiLocal = this._dateService.lisaaPaiviaPaikallinen(this._dateService.currentLocalDate(), 14)
            const voimassaAsti = this._dateService.localDateToDate(voimassaAstiLocal)

            const dialogData: AsiakasKyselyDialogData = {
              kayttajaUid: kayttajanTiedot.uid,
              kayttajaAsiakasAvain: kayttajanTiedot.asiakasAvain,
              voimassaAsti: voimassaAsti,
            }
            const dialogConfig: MatDialogConfig<AsiakasKyselyDialogData> = {
              data: dialogData,
              autoFocus: false,
            }
            return dialogConfig
          }),
        )
      }),
    )

    // Configure dialogs

    this._allowedDialogsService.registerDialog<TervetuloaSopimusV2Dialog, TervetuloaSopimusV2DialogData>(TervetuloaSopimusV2Dialog, 'tervetuloa', 9000, naytaTervetuloaDialogObservable)
    this._allowedDialogsService.registerDialog<TunnistautuminenNetsDialog, TunnistautuminenNetsDialogData>(
      TunnistautuminenNetsDialog,
      'tunnistautuminen-nets',
      8000,
      naytaNetsTunnistautuminenDialogObservable,
    )
    // this._allowedDialogsService.registerDialog<UusiSopimusDialog, UusiSopimusDialogData>(
    //   UusiSopimusDialog,
    //   'uusi-sopimus',
    //   3000,
    //   naytaSopimusDialogObservable
    // )
    this._allowedDialogsService.registerDialog<MuutoksiaPalveluunDialog, MuutoksiaPalveluunDialogData>(MuutoksiaPalveluunDialog, 'muutoksia-palveluun', 3000, naytaMuutoksiaPalveluunDialog)
    // this._allowedDialogsService.registerDialog<ViiaLoginRequiredDialog, AiiaDialogData>(
    //   ViiaLoginRequiredDialog,
    //   BankDialogTypes.PSD2_RENEW,
    //   2000,
    //   isAiiaLoginRequiredObservable // TODO! Start checking this from BankConsentDialog, not Aiia-specific consent
    // )
    // GENERAL
    this._allowedDialogsService.registerDialog<GeneralBankSelectionDialog, GeneralBankSelectionDialogData>(
      GeneralBankSelectionDialog,
      BankDialogType.GENERAL,
      1999,
      showGeneralBankSelectionDialogObservable,
    )
    // // GENERAL WITHOUT HOLVI
    // this._allowedDialogsService.registerDialog<GeneralBankSelectionDialog, GeneralBankSelectionDialogData>(
    //   GeneralBankSelectionDialog,
    //   BankDialogTypes.GENERAL_WO_HOLVI,
    //   1998,
    //   showGeneralWithoutHolviConnectionDialogObservable
    // )
    // HOLVI
    this._allowedDialogsService.registerDialog<HolviLoginRequiredDialog, HolviLoginRequiredDialogData>(HolviLoginRequiredDialog, BankDialogType.HOLVI_RENEW, 1997, showHolviConnectionDialogObservable)
    // AIIA
    this._allowedDialogsService.registerDialog<ViiaLoginRequiredDialog, AiiaDialogData>(ViiaLoginRequiredDialog, BankDialogType.PSD2_RENEW, 1996, showAiiaConnectionDialogObservable)

    // ENABLE BANKING
    this._allowedDialogsService.registerDialog<EnableBankingLoginRequiredDialog, EnableBankingDialogData>(
      EnableBankingLoginRequiredDialog,
      BankDialogType.ENABLE_BANKING,
      1995,
      showEnableBankingDialogObservable,
    )

    this._allowedDialogsService.registerDialog<UudetTuntemistiedotDialog, UudetTuntemistiedotDialogData>(UudetTuntemistiedotDialog, 'uudet-tuntemistiedot', 1000, naytaTuntemistiedotDialogObservable)

    // this._allowedDialogsService.registerDialog<MaksutMainosLaskujenVastaanottoDialog, MaksutMainosLaskujenVastaanottoDialogData>(
    //   MaksutMainosLaskujenVastaanottoDialog,
    //   'mainos-vastaanota-laskuja',
    //   500,
    //   naytaLaskujenVastaanottoMainosDialogObservable
    // )

    this._allowedDialogsService.registerDialog<AsiakasKyselyDialog, AsiakasKyselyDialogData>(AsiakasKyselyDialog, 'asiakaskysely', 500, naytaAsiakasKyselyDialogObservable)

    this._allowedDialogsService.startShowingDialogs()
  }

  private _naytaTunnistamisDialog(kayttaja: Kayttaja): boolean {
    if (!kayttaja) {
      return false
    }
    const tunnistetaanKoskaOllaanPalaamassaTunnistautumisesta =
      this._tunnistautuminenService.getTunnistautuminenAction() === 'continue-standalone-dialog' && !!this._tunnistautuminenService.getTunnistautuminenState() && kayttaja.roolit.TUNNISTETAAN
    const tunnistetaanKoskaPyydettyEikaVielaVahva = (kayttaja.kayttajaTunnistettu === 'ei-tunnistettu' || kayttaja.kayttajaTunnistettu === 'tunnistettu-hetulla') && kayttaja.roolit.TUNNISTETAAN
    const tunnistettuMuttaKayttajanPepTiedotEiAnnettu = kayttaja.roolit.TUNNISTETAAN && kayttaja.kayttajaTunnistettu === 'tunnistettu-nets' && !kayttaja.pepTiedotAnnettu
    return tunnistetaanKoskaPyydettyEikaVielaVahva || tunnistetaanKoskaOllaanPalaamassaTunnistautumisesta || tunnistettuMuttaKayttajanPepTiedotEiAnnettu
  }

  private _naytaSopimusDialog(kayttaja: Kayttaja): boolean {
    if (!kayttaja) {
      return false
    }
    return kayttaja.roolit.HALLINTO_SOPIMUKSEN_HYVAKSYJA && kayttaja.sopimuksenHyvaksyntaStatus === 'ei-hyvaksytty'
  }

  private _naytaTuntemistiedotDialogi(asiakas: Asiakas, kayttaja: Kayttaja): boolean {
    if (
      kayttaja &&
      asiakas &&
      kayttaja.roolit.HALLINTO_TUNTEMISTIETOJEN_ANTAJA &&
      (asiakas.tuntemistietojenStatus === 'vaatii-paivityksen' || asiakas.tuntemistietojenStatus === 'ei-annettu') &&
      (asiakas.yritysmuoto === Yritysmuoto.OSAKEYHTIO || asiakas.yritysmuoto === Yritysmuoto.OSUUSKUNTA)
    ) {
      return true
    }
    return false
  }

  muutaNayta() {
    this.avattuNappulasta.next(!this.avattuNappulasta.value)
  }

  signalChange() {
    this._windowSizeService.publishChange()
  }

  suljeVasenValikko() {
    this.avattuNappulasta.next(false)
  }

  ngOnInit() {
    // Bodyyn lisätään luokka jotta page-content saa padding-top:n eikä jää bannerin alle.
    // Käytetään document.body:tä eikä .valikko-sisalto:a, koska osa sivuista käyttää omaa layouttiaan eikä ole valikko-sisallon sisällä.
    // Kirjautumistila on yhteinen saman selaimen välilehdille: yrityksen vaihto toisessa välilehdessä vaihtaa käyttäjän täälläkin,
    // mutta näkymien ja lomakkeiden tila on yhä edellisen yrityksen. Uudelleenlataus estää tallentamasta sitä väärälle yritykselle.
    this._firebaseLemonaid.authUserObservable
      .pipe(
        map(user => user?.uid ?? null),
        pairwise(),
        filter(([edellinen, nykyinen]) => !!edellinen && !!nykyinen && edellinen !== nykyinen),
        takeUntil(this._ngUnsubscribe),
      )
      .subscribe(() => window.location.assign('/'))

    this.naytaImpersonointiBanneriObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(nayta => {
      if (nayta) {
        this._renderer.addClass(document.body, 'impersonointi-aktiivinen')
      } else {
        this._renderer.removeClass(document.body, 'impersonointi-aktiivinen')
      }
    })
    this.naytettavaViestiObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(viesti => {
      if (viesti) {
        this._renderer.addClass(document.body, 'lemonaid-viesti-nakyvissa')
      } else {
        this._renderer.removeClass(document.body, 'lemonaid-viesti-nakyvissa')
      }
    })
  }

  private _shouldShowBankDialog = (
    consent: BankCustomerConsent | null,
    dialogTypes: BankDialogType[],
    dialogService: { postponedRecently?: () => boolean; [key: string]: any } | null = null,
  ): boolean => {
    if (!consent) {
      return false
    }

    if (consent.dialogCompletePreventReShow?.some(prevented => dialogTypes.includes(prevented))) {
      return false
    }

    if (dialogService?.postponedRecently?.()) {
      return false
    }

    if (dialogService?.flowCompletedRecently?.()) {
      return null
    }

    if (dialogTypes.some(type => consent.forceDialogToShow === type)) {
      return true
    }

    return consent.dialogsToShow?.some(dialog => dialogTypes.includes(dialog.dialogToShow)) ?? false
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  // private _isViiaLoginRequired(asiakas: Asiakas): Observable<boolean> {
  //   return this._firebaseLemonaid.firestoreDoc<ViiaAsiakkaanAutentikointitiedot>(this._viiaUriService.annaAsiakkaanAutentikointitietojenUri(asiakas.asiakasAvain))
  //     .listen().pipe(
  //       map(consent => {
  //         if (!consent) {
  //           return false // Consent is created when the first viia role user is added
  //         }
  //         // Don't require login if dialog has been postponed
  //         if (consent.postponedUntil && this._dateService.compareLocalDates(consent.postponedUntil, '>', this._dateService.currentLocalDate())) {
  //           return false
  //         }
  //         // Has Viia login been required by backend?
  //         return this._dateService.compareLocalDates(consent.loginRequiredFrom, '<=', this._dateService.currentLocalDate())
  //       })
  //     )
  // }
}
